import { z } from 'zod';

import { SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import type { TokenSearchResponse, TokensResponse } from '@/utils/0x/token-registry.types';
import { isEthereumAddress, isSolanaAddress } from '@/utils/models';

export const SEARCH_TREND_RECEIPT_COOKIE = 'matcha-search-receipt';
export const SEARCH_TREND_QUERY_PARAM = '__matcha_search';

/** Token identity exchanged with the backend, never a frontend token key. */
export type TrendToken = { chainId: number; address: string };
/** Active wallets reported by the client; not proof of ownership. */
export type TrendWallets = { ethereum?: string; solana?: string };
/** Search surfaces whose selections navigate to token profiles. */
export type TrendSource = 'header' | 'home_page';

/** Public receipt metadata; the redemption secret is never exposed here. */
export const searchTrendReceiptSchema = z
  .object({ id: z.string().uuid(), expiresAt: z.number().int().nonnegative() })
  .strict();

/** Identifies the latest issued receipt without granting redemption. */
export type SearchTrendReceipt = z.infer<typeof searchTrendReceiptSchema>;

/** Private lookup input; candidate tokens and visitor identity are server-owned. */
export const searchTrendLookupRequestSchema = z
  .object({
    query: z.string().trim().min(1).max(256),
    chainIds: z
      .array(
        z
          .number()
          .int()
          .refine((chainId) => SUPPORTED_CHAIN_IDS.includes(chainId)),
      )
      .min(1)
      .max(SUPPORTED_CHAIN_IDS.length)
      .transform((chainIds) => [...new Set(chainIds)]),
    limit: z.number().int().min(1).max(100).default(30),
    page: z.number().int().min(1).default(1),
    wallets: z
      .object({
        ethereum: z.string().toLowerCase().refine(isEthereumAddress).optional(),
        solana: z.string().refine(isSolanaAddress).optional(),
      })
      .strict()
      .refine((wallets) => Boolean(wallets.ethereum || wallets.solana)),
    source: z.enum(['header', 'home_page']),
  })
  .strict();

/** Browser request before server normalization and pagination defaults. */
export type SearchTrendLookupRequest = z.input<typeof searchTrendLookupRequestSchema>;
/** Existing token results plus optional public attribution metadata. */
export type SearchTrendLookupResponse = {
  results: TokenSearchResponse | TokensResponse;
  receipt: SearchTrendReceipt | null;
};
/** Binds a receipt to the immutable wallet snapshot used for its lookup. */
export type SearchReceiptContext = SearchTrendReceipt & { walletsKey: string };
/** Cached search results, optionally carrying selection attribution. */
export type SearchTokensPage = (TokenSearchResponse | TokensResponse) & {
  searchReceipt?: SearchReceiptContext;
};

/** Stable wallet identity, with EVM case folded and Solana case preserved. */
export const searchTrendWalletsKey = (wallets: TrendWallets): string =>
  JSON.stringify([wallets.ethereum?.toLowerCase() ?? null, wallets.solana ?? null]);
